import { Box, MenuItem, Select } from "@mui/material";
import { SelectChangeEvent, type SelectProps } from "@mui/material/Select";
import { type SxProps, type Theme } from "@mui/material/styles";
import React, { useMemo } from "react";

import { countries, type Country } from "../../utils/countries";

type BaseSelectProps = Omit<
  SelectProps<string>,
  "value" | "onChange" | "renderValue" | "variant"
>;

export interface CountryCodeSelectProps extends BaseSelectProps {
  /** ISO country code (e.g., "US"). Defaults to US. */
  value?: string;
  /** Called with the selected country object */
  onChange?: (country: Country | null) => void;
  /** Optional shorthand to receive the code directly */
  onCodeChange?: (code: string | undefined, country: Country | null) => void;
  /** Form field name */
  name?: string;
  /** Compact spacing for tight layouts (e.g., phone adornments) */
  compact?: boolean;
  /** Override select slot styles */
  selectSx?: SxProps<Theme>;
  /** Max height for dropdown menu */
  menuMaxHeight?: number;
}

const CountryCodeSelect: React.FC<CountryCodeSelectProps> = ({
  value = "US",
  onChange,
  onCodeChange,
  name = "countryCode",
  compact = false,
  selectSx,
  menuMaxHeight = 400,
  sx,
  ...rest
}) => {
  const countryLookup = useMemo(() => {
    const map = new Map<string, Country>();
    countries.forEach((c) => map.set(c.code, c));
    return map;
  }, []);

  const findCountry = (code?: string): Country | null =>
    code ? countryLookup.get(code) ?? null : null;

  const handleChange = (event: SelectChangeEvent<string>) => {
    const code = event.target.value as string;
    const country = findCountry(code);
    onChange?.(country);
    onCodeChange?.(country?.code, country);
  };

  const renderValue = (val: unknown) => {
    const option = findCountry(val as string);
    if (!option) return null;
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: compact ? 0.25 : 0.5 }}>
        <img
          loading="lazy"
          width={compact ? 18 : 20}
          height={compact ? 12 : 14}
          src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
          srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
          alt=""
        />
        <span style={{ fontSize: compact ? 11 : 12 }}>
          +{option.phone} ({option.code})
        </span>
      </Box>
    );
  };

  const baseSx: SxProps<Theme> = {
    minWidth: compact ? 70 : 90,
    "& .MuiSelect-select": {
      p: 0,
      display: "flex",
      alignItems: "center",
      backgroundColor: "transparent",
    },
    "& .MuiSelect-select:focus": {
      backgroundColor: "transparent",
    },
    "& .MuiSelect-icon": { display: "none" },
  };

  const combinedSx: SxProps<Theme> = [baseSx, selectSx, sx].filter(
    Boolean,
  ) as SxProps<Theme>;

  return (
    <Select
      variant="standard"
      value={value}
      onChange={handleChange}
      disableUnderline
      inputProps={{ name }}
      renderValue={renderValue}
      sx={combinedSx}
      MenuProps={{
        PaperProps: { style: { maxHeight: menuMaxHeight } },
      }}
      {...rest}
    >
      {countries.map((option) => (
        <MenuItem key={option.code} value={option.code}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <img
              loading="lazy"
              width={20}
              src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
              srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
              alt=""
            />
            {option.label} (+{option.phone})
          </Box>
        </MenuItem>
      ))}
    </Select>
  );
};

export default CountryCodeSelect;
